import type { User } from '@/types/user';

const CIAL_EMAIL_DOMAIN = '@cialdnb.com';

/**
 * Keys for feature toggles. The backend decides who has access to each key
 * (see `backend/services/feature_toggle_service.py`) and reports it on the
 * logged-in user as `enabled_features`; the frontend only reads that list —
 * it never re-derives access itself. Keys here must match the backend's
 * `FEATURE_KEYS`.
 */
export type FeatureKey = 'pulse' | 'kyt' // | 'estimated_revenue' | 'global_screening';

/**
 * Whether the given user is allowed to access the given feature toggle.
 * Access is decided by the backend and reported on `user.enabled_features`.
 */
export const hasFeatureAccess = (
    user: Pick<User, 'enabled_features'> | null | undefined,
    feature: FeatureKey
): boolean => {
    return !!user?.enabled_features?.includes(feature);
};

/**
 * Whether the given email belongs to an internal CIAL D&B user.
 * Used to gate features that are not yet released to external customers.
 */
export const isCialInternalUser = (email: string | null | undefined): boolean => {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) {
        return false;
    }

    return normalizedEmail.endsWith(CIAL_EMAIL_DOMAIN);
};

export const hasMonitoringConfigAccess = (email: string | null | undefined): boolean => {
    return isCialInternalUser(email);
};
